<script setup lang="ts">
/**
 * Renders an `error`-type part (a turn that failed or was cancelled). Cancelled
 * turns arrive as an error part whose text the reducer sets to a cancellation
 * notice; we surface both with the same muted-destructive treatment.
 */
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import type { ChatError } from "../../types/chat";

const props = defineProps<{
  text?: string;
  error?: ChatError;
}>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => props.error?.title || props.text || t("fallback"));
const detail = computed(() => props.error?.detail || (props.error ? props.text : undefined));
</script>

<template>
  <div
    class="rounded-lg border border-destructive/25 bg-destructive/10 px-3 py-3 text-sm text-foreground"
    data-slot="chat-error-part"
    role="alert"
  >
    <div class="flex items-start gap-2">
      <Icon name="heroicons:exclamation-triangle" mode="svg" class="mt-0.5 size-4 shrink-0 text-destructive" />
      <div class="min-w-0 flex-1 space-y-1">
        <p class="break-words font-medium text-destructive">{{ title }}</p>
        <p v-if="detail" class="break-words text-muted-foreground">{{ detail }}</p>
      </div>
    </div>

    <div v-if="error?.actions.length" class="mt-3 space-y-2 border-t border-destructive/15 pt-3">
      <div
        v-for="action in error.actions"
        :key="action.id"
        class="rounded-md border bg-background/70 p-2.5"
      >
        <div class="flex flex-wrap items-start justify-between gap-2">
          <div class="min-w-0">
            <p class="font-medium">{{ action.label }}</p>
            <p v-if="action.description" class="mt-0.5 text-xs text-muted-foreground">
              {{ action.description }}
            </p>
          </div>
          <NuxtLink
            v-if="action.href"
            :to="action.href"
            class="inline-flex shrink-0 items-center gap-1 rounded-md border bg-background px-2 py-1 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {{ t("openSettings") }}
            <Icon name="heroicons:arrow-top-right-on-square" mode="svg" class="size-3.5" />
          </NuxtLink>
        </div>
        <dl v-if="action.setting || action.recommendedValue" class="mt-2 grid gap-1 text-xs sm:grid-cols-2">
          <div v-if="action.setting">
            <dt class="text-muted-foreground">{{ t("setting") }}</dt>
            <dd class="break-all font-mono">{{ action.setting }}</dd>
          </div>
          <div v-if="action.recommendedValue">
            <dt class="text-muted-foreground">{{ t("recommended") }}</dt>
            <dd class="break-words font-medium">{{ action.recommendedValue }}</dd>
          </div>
        </dl>
      </div>
    </div>

    <details v-if="error?.referenceId || error?.code" class="mt-3 border-t border-destructive/15 pt-2 text-xs">
      <summary class="cursor-pointer select-none font-medium text-muted-foreground">
        {{ t("technicalDetails") }}
      </summary>
      <dl class="mt-2 grid gap-1 text-muted-foreground">
        <div v-if="error.code" class="flex gap-2">
          <dt>{{ t("code") }}</dt>
          <dd class="font-mono text-foreground">{{ error.code }}</dd>
        </div>
        <div v-if="error.referenceId" class="flex gap-2">
          <dt>{{ t("reference") }}</dt>
          <dd class="break-all font-mono text-foreground">{{ error.referenceId }}</dd>
        </div>
      </dl>
    </details>
  </div>
</template>

<i18n lang="yaml">
en:
  fallback: Something went wrong.
  openSettings: Open settings
  setting: Setting
  recommended: Recommended value
  technicalDetails: Technical details
  code: "Code:"
  reference: "Reference:"
</i18n>
